// Day Drawer's header block — the 3 badges (Training/Rest, Plan, Source),
// the target box, and the supplementary stat chips (weight/steps/sleep/...).
// Split out of DayDrawer.js, which was growing into a 9-component,
// ~300-line file mixing header presentation with orchestration — mirrors
// how the rest of Nutrition Progress already splits by responsibility
// (TableCells.js/TableRows.js/TableHead.js/TableFormat.js instead of one
// giant TableView.js). DayDrawer.js stays a pure resolver + orchestrator;
// this file owns everything under the day's title down to the tab bar.
import PropTypes from "prop-types";
import { useMemo } from "react";

import SvgIcon from "../../../../../parts/SvgIcon";
import { getPlanTargetsSummary, resolveFoodLogSource } from "../../../../../utils/nutrition-progress";
import { fmtSleep, toDisplayWeight, translateDayDrawer as d } from "./viewTokens";
import { DUMBBELL_ICON, REST_ICON } from "./TableFormat";

const SCALE_ICON = "/img/new-icons/scale.svg";
const STEPS_ICON = "/img/new-icons/steps.svg";
const SLEEP_ICON = "/img/new-icons/sleep.svg";
const CAMERA_ICON = "/img/svg/camera.svg";
// Same icons LogVitalsCard/LogReadinessCard/LogSessionRpeCard already use
// for these exact fields — reused here, not invented, so a coach sees the
// same glyph for "HRV" (etc.) whether they're looking at the drawer or the
// real Log view.
const WATER_ICON = "/img/new-icons/drop-icon.svg";
const HEART_PULSE_ICON = "/img/new-icons/heart-pulse-solid.svg"; // hrv + resting heart rate (LogVitalsCard uses the same icon for both)
const BODY_FAT_ICON = "/img/new-icons/percentage-square.svg";
const READINESS_ICON = "/img/new-icons/heart-circle-bolt-solid.svg";
const RPE_ICON = "/img/new-icons/barbell.svg"; // LogSessionRpeCard's own icon — distinct from DUMBBELL_ICON (dumbbell.svg), used for the Training/Rest badge below
const OZ_PER_L = 33.8140227; // LogVitalsCard's own conversion factor, duplicated for the same imperial-units formatting

/* ---- header badges ---------------------------------------------------- */

// Renders the Day Type Badge
const DayTypeBadge = ({ workout, t }) => (
  <span className={`np-drawer__badge np-drawer__badge--${workout ? "training" : "rest"}`}>
    <SvgIcon
      src={workout ? DUMBBELL_ICON : REST_ICON}
      useCurrentColor
      classNames="np-drawer__badge-icon"
    />
    {workout ? d(t, "trainingBadge") : d(t, "restBadge")}
  </span>
);

// Renders the Plan Badge
const PlanBadge = ({ plan, t }) => {
  if (!plan) return null;
  return (
    <span className="np-drawer__badge np-drawer__badge--plan">
      {d(t, "planBadge", { range: getPlanTargetsSummary(plan).label })}
    </span>
  );
};

const SOURCE_BADGE_LABEL = {
  mealPlan: (t) => d(t, "mealPlanBadge"),
  synced: (t, sourceInfo) => t("foodLog.syncedVia", { source: sourceInfo.label }),
  manual: (t) => d(t, "manualEntryBadge"),
};

// Renders the Source Badge
const SourceBadge = ({ sourceInfo, t }) => {
  if (!sourceInfo) return null;
  const label = SOURCE_BADGE_LABEL[sourceInfo.token](t, sourceInfo);
  return <span className="np-drawer__badge np-drawer__badge--source">{label}</span>;
};

/* ---- target box --------------------------------------------------------- */

// Renders the Target Box
const TargetBox = ({ row, plan, t }) => {
  if (!row?.target || !plan) return null;
  const target = row.target;
  const kcalAbbr = t("common:units.kcalAbbr");
  const proteinAbbr = t("common:macros.proteinAbbr");
  const carbsAbbr = t("common:macros.carbsAbbr");
  const fatAbbr = t("common:macros.fatAbbr");
  return (
    <div className="np-drawer__target">
      <p className="np-drawer__target-label">
        {d(t, row.workout ? "trainingTargetLabel" : "restTargetLabel")} ·{" "}
        {d(t, "planPrefix")} {getPlanTargetsSummary(plan).label}
      </p>
      <p className="np-drawer__target-value">
        {Math.round(target.kcal).toLocaleString()} {kcalAbbr} · {Math.round(target.proteins)} {proteinAbbr} ·{" "}
        {Math.round(target.carbs)} {carbsAbbr} · {Math.round(target.fats)} {fatAbbr}
      </p>
    </div>
  );
};

/* ---- supplementary chips -------------------------------------------------- */

// Renders the Stat Chip
const StatChip = ({ icon, label, value }) => {
  if (value == null) return null;
  return (
    <div className="np-drawer__chip">
      <SvgIcon src={icon} useCurrentColor classNames="np-drawer__chip-icon" />
      <span className="np-drawer__chip-label">{label}</span>
      <span className="np-drawer__chip-value">{value}</span>
    </div>
  );
};

// Same formatting LogVitalsCard.js uses for waterIntake (stored in liters).
const formatWater = (liters, isImperial, t) => {
  if (!liters) return null;
  if (isImperial) return `${Math.round(liters * OZ_PER_L)} ${t("common:units.ozAbbr")}`;
  return `${Math.round(liters * 10) / 10} ${t("common:units.literAbbr")}`;
};

// Renders the Supplementary Chips
const SupplementaryChips = ({ row, units, t }) => {
  const isImperial = units === "imperial";
  const weightUnitLabel = isImperial ? "lb" : "kg";
  const displayWeight = toDisplayWeight(row.weight, isImperial);

  // Config-driven instead of 8 hand-written <StatChip> blocks — same
  // array-of-rows pattern already used by DayDrawerNutritionCard's ROWS.
  const chips = [
    {
      key: "weight",
      icon: SCALE_ICON,
      label: d(t, "weight"),
      value: displayWeight == null ? null : `${displayWeight} ${weightUnitLabel}`,
    },
    {
      key: "steps",
      icon: STEPS_ICON,
      label: d(t, "steps"),
      value: row.steps == null ? null : Math.round(row.steps).toLocaleString(),
    },
    {
      key: "sleep",
      icon: SLEEP_ICON,
      label: d(t, "sleep"),
      value: row.sleep == null ? null : fmtSleep(row.sleep),
    },
    {
      key: "photos",
      icon: CAMERA_ICON,
      label: d(t, "photos"),
      value: row.photos > 0 ? row.photos : null,
    },
    {
      key: "hrv",
      icon: HEART_PULSE_ICON,
      label: t("calendar.cards.vitals.hrv"),
      value: row.hrv == null ? null : `${Math.round(row.hrv)}${t("common:units.msAbbr")}`,
    },
    {
      key: "restingHeartRate",
      icon: HEART_PULSE_ICON,
      label: t("metrics.restingHeartRate"),
      value:
        row.restingHeartRate == null
          ? null
          : `${Math.round(row.restingHeartRate)} ${t("restingHeartRate.unit")}`,
    },
    {
      key: "readiness",
      icon: READINESS_ICON,
      label: d(t, "readiness"),
      value: row.readinessScore == null ? null : `${Math.round(row.readinessScore)}/25`,
    },
    {
      key: "sessionRPE",
      icon: RPE_ICON,
      label: t("calendar.cards.sessionRpe.title"),
      // Not rounded — RPE genuinely uses half-point increments (7.5, 8.5,
      // ...) in this domain, and LogSessionRpeCard.js (the real card)
      // doesn't round it either.
      value: row.sessionRPE == null || row.sessionRPE <= 0 ? null : `${row.sessionRPE}/10`,
    },
    {
      key: "water",
      icon: WATER_ICON,
      label: t("calendar.cards.vitals.water"),
      value: formatWater(row.waterIntake, isImperial, t),
    },
    {
      key: "bodyFat",
      icon: BODY_FAT_ICON,
      label: t("metrics.bodyFat"),
      value: row.bodyFatPercentage ? `${Math.round(row.bodyFatPercentage * 10) / 10}%` : null,
    },
  ];

  return (
    <div className="np-drawer__chips">
      {chips.map(({ key, icon, label, value }) => (
        <StatChip key={key} icon={icon} label={label} value={value} />
      ))}
    </div>
  );
};

/* ---- composite export ---------------------------------------------------- */

const DayDrawerHeader = ({ row, plan, stat, units, t }) => {
  const sourceInfo = useMemo(() => resolveFoodLogSource(stat), [stat]);
  return (
    <>
      <div className="np-drawer__badges">
        <DayTypeBadge workout={row.workout} t={t} />
        <PlanBadge plan={plan} t={t} />
        <SourceBadge sourceInfo={sourceInfo} t={t} />
      </div>

      <TargetBox row={row} plan={plan} t={t} />

      <SupplementaryChips row={row} units={units} t={t} />
    </>
  );
};

DayDrawerHeader.propTypes = {
  row: PropTypes.object.isRequired,
  plan: PropTypes.object,
  stat: PropTypes.object,
  units: PropTypes.string,
  t: PropTypes.func.isRequired,
};

export default DayDrawerHeader;
